// context/CartContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import * as Sentry from '@sentry/react';

// Import utilities
import { getItem, setItem, removeItem } from 'utils/localStorageUtils';
import {
  calculateBasePrice,
  calculateItemPrice,
  calculateSubtotal,
  calculateCartTotal,
  formatPrice,
} from 'utils/cartCalculator';
import {
  normalizeModifiers,
  formatModifiersForClover,
  areModifiersEqual,
} from 'utils/modifierUtils';

// Create the cart context
const CartContext = createContext();

// Custom hook to use the cart contex
//
export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

// One cart row, in the shape every consumer expects (baseItem for Clover,
// modifications for the order payload, price/subtotal for display). addToCart
// and updateCartItem (#583) both build rows here so a replaced row is
// indistinguishable from one added fresh.
const buildCartItem = (item, quantity, itemModifiers, cartItemId) => {
  const basePrice = calculateBasePrice(item);
  const totalPrice = calculateItemPrice(item, itemModifiers);
  const subtotal = calculateSubtotal(
    { ...item, price: totalPrice, quantity },
    null,
  );

  return {
    cartItemId: cartItemId || generateCartItemId(),
    quantity,
    baseItem: {
      id: item.id,
      name: item.name || '',
      price: basePrice,
    },
    // Format modifiers for Clover API
    modifications: formatModifiersForClover(itemModifiers),
    // Display data
    name: item.name || '',
    price: totalPrice,
    subtotal,
    displayPrice: item.displayPrice,
    variantInfo: item.variantInfo || '',
    modifiers: itemModifiers,
    originalItem: item,
  };
};

// Helper to generate a unique cart item ID
const generateCartItemId = () => {
  return `item_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
};

// Cart provider component
export const CartProvider = ({ children }) => {
  // Array of cart items with full details including proper modifier structure
  const [cartItems, setCartItems] = useState([]);
  // Track if cart has been loaded from localStorage
  const [cartLoaded, setCartLoaded] = useState(false);

  // Add Sentry breadcrumb for tracking - reduced to only critical operations
  const addBreadcrumb = (message, data = {}) => {
    Sentry.addBreadcrumb({
      message,
      category: 'cart',
      level: 'info',
      data,
    });
  };

  // Track errors in Sentry
  const trackError = (error, context = {}) => {
    Sentry.captureException(error, {
      tags: {
        component: 'CartContext',
      },
      extra: context,
    });
  };

  // Load cart from localStorage on initial render with error handling
  useEffect(() => {
    try {
      const savedCart = getItem('cart', []);
      if (savedCart && Array.isArray(savedCart) && savedCart.length > 0) {
        setCartItems(savedCart);
        addBreadcrumb('Cart loaded from localStorage', {
          itemCount: savedCart.length,
        });
      }
    } catch (error) {
      trackError(error, {
        operation: 'loadCart',
      });
      // Graceful degradation - continue with empty cart
      setCartItems([]);
    } finally {
      // Mark cart as loaded regardless of success/failure
      setCartLoaded(true);
    }
  }, []);

  // Save cart to localStorage when it changes with error handling
  useEffect(() => {
    try {
      if (cartItems.length > 0) {
        setItem('cart', cartItems);
        // Reduced logging - only log major state changes
        if (cartItems.length % 5 === 0) {
          // Only log every 5th item for performance
          addBreadcrumb('Cart saved to localStorage', {
            itemCount: cartItems.length,
          });
        }
      } else {
        removeItem('cart');
        addBreadcrumb('Cart cleared from localStorage');
      }
    } catch (error) {
      trackError(error, {
        operation: 'saveCart',
      });
      // Continue without crashing the app
    }
  }, [cartItems]);

  // Add an item to the cart with proper Clover-compatible structure.
  // No default on quantityOrModifiers: it must stay `undefined` when the caller
  // passes only the item, so the quantity can fall back to item.quantity (the
  // value the modifier modal sets). A default of 1 here silently ignored
  // item.quantity and added a single unit — see #406.
  const addToCart = (item, quantityOrModifiers, modifiers = []) => {
    try {
      // Basic validation
      if (!item || !item.id) {
        trackError(new Error('Invalid item, cannot add to cart'), {
          operation: 'addToCart',
          item,
        });
        return;
      }

      // Handle backward compatibility: if second param is array, it's modifiers
      let quantity;
      let itemModifiers;
      if (Array.isArray(quantityOrModifiers)) {
        quantity = item.quantity || 1;
        itemModifiers = normalizeModifiers(item, quantityOrModifiers);
      } else {
        quantity = quantityOrModifiers ?? item.quantity ?? 1;
        itemModifiers = normalizeModifiers(item, modifiers);
      }

      // Add breadcrumb for tracking (only critical info)
      addBreadcrumb('Add to cart attempt', {
        itemId: item.id,
        itemName: item.name,
        quantity: quantity,
      });

      const newCartItem = buildCartItem(item, quantity, itemModifiers);

      // Check if this exact item already exists in the cart
      setCartItems((prev) => {
        // Find an identical item (same base item ID and identical modifiers)
        const existingItemIndex = prev.findIndex((cartItem) => {
          return (
            cartItem.baseItem.id === item.id &&
            areModifiersEqual(itemModifiers, normalizeModifiers(cartItem))
          );
        });

        // If we found an existing identical item, increment its quantity
        if (existingItemIndex >= 0) {
          const updatedItems = [...prev];
          const existingItem = updatedItems[existingItemIndex];
          const newQuantity = existingItem.quantity + quantity;

          // Use utility for consistent calculation
          const updatedSubtotal = calculateSubtotal(
            { ...existingItem, quantity: newQuantity },
            normalizeModifiers(existingItem),
          );

          // Only log important changes
          if (newQuantity % 5 === 0) {
            addBreadcrumb('Incrementing existing item quantity', {
              itemId: existingItem.baseItem.id,
              newQuantity: newQuantity,
            });
          }

          updatedItems[existingItemIndex] = {
            ...existingItem,
            quantity: newQuantity,
            subtotal: updatedSubtotal,
          };

          return updatedItems;
        }

        // Add as a new item
        return [...prev, newCartItem];
      });
    } catch (error) {
      trackError(error, {
        operation: 'addToCart',
        item,
        modifiers,
      });
      // This would be a good place to add toast notification
    }
  };

  // Replace one row with a re-customised version of the same item (#583).
  // The stale-modifier recovery at checkout re-opens the modal for a row
  // whose modifier vanished from the menu; saving from there must land the
  // corrected row where the old one sat, under the same cartItemId, rather
  // than appending a duplicate. A row that no longer exists is added instead
  // of dropped — the customer just chose it.
  const updateCartItem = (cartItemId, item, modifiers = []) => {
    try {
      if (!item || !item.id) {
        trackError(new Error('Invalid item, cannot update cart'), {
          operation: 'updateCartItem',
          cartItemId,
          item,
        });
        return;
      }

      const quantity = item.quantity ?? 1;
      const itemModifiers = normalizeModifiers(item, modifiers);

      setCartItems((prev) => {
        const index = prev.findIndex((row) => row.cartItemId === cartItemId);
        if (index < 0) {
          return [...prev, buildCartItem(item, quantity, itemModifiers)];
        }
        const next = [...prev];
        next[index] = buildCartItem(item, quantity, itemModifiers, cartItemId);
        return next;
      });
    } catch (error) {
      trackError(error, {
        operation: 'updateCartItem',
        cartItemId,
        item,
      });
    }
  };

  // Update quantity of an item in the cart
  const updateCartItemQuantity = (cartItemId, newQuantity) => {
    try {
      if (newQuantity <= 0) {
        // Remove the item if quantity is zero or negative
        removeCartItem(cartItemId);
        return;
      }

      setCartItems((prevItems) =>
        prevItems.map((item) => {
          if (item.cartItemId === cartItemId) {
            // Use calculator utility for consistent pricing
            const updatedSubtotal = calculateSubtotal(
              { ...item, quantity: newQuantity },
              null,
            );
            return {
              ...item,
              quantity: newQuantity,
              subtotal: updatedSubtotal,
            };
          }
          return item;
        }),
      );

      addBreadcrumb('Cart item quantity updated', {
        cartItemId,
        newQuantity,
      });
    } catch (error) {
      trackError(error, {
        operation: 'updateCartItemQuantity',
        cartItemId,
        newQuantity,
      });
    }
  };

  // Remove a specific item from the cart
  const removeCartItem = (cartItemId) => {
    try {
      const itemToRemove = cartItems.find(
        (item) => item.cartItemId === cartItemId,
      );

      setCartItems((prevItems) =>
        prevItems.filter((item) => item.cartItemId !== cartItemId),
      );

      addBreadcrumb('Item removed from cart', {
        cartItemId,
        itemName: itemToRemove?.name || 'unknown',
      });
    } catch (error) {
      trackError(error, {
        operation: 'removeCartItem',
        cartItemId,
      });
    }
  };

  // Clear the entire cart
  const clearCart = () => {
    try {
      setCartItems([]);
      removeItem('cart');

      addBreadcrumb('Cart cleared', {
        previousItemCount: cartItems.length,
      });
    } catch (error) {
      trackError(error, {
        operation: 'clearCart',
      });
    }
  };

  // Calculate total number of items in cart
  const getTotalItems = () => {
    try {
      return cartItems.reduce((total, item) => total + (item.quantity || 0), 0);
    } catch (error) {
      trackError(error, {
        operation: 'getTotalItems',
      });
      return 0;
    }
  };

  // Get formatted cart data for Clover Atomic Order API
  const getCloverOrderData = () => {
    const expandedLineItems = [];

    cartItems.forEach((item) => {
      // Create separate line items for each quantity
      for (let i = 0; i < item.quantity; i++) {
        expandedLineItems.push({
          item: {
            id: item.baseItem.id,
          },
          exchanged: 'false',
          refunded: 'false',
          isRevenue: 'false',
          modifications: formatModifiersForClover(normalizeModifiers(item)),
          amount: calculateItemPrice(item, normalizeModifiers(item)),
          unitQty: 1000, // Clover stores quantity in thousandths (1000 = 1 unit)
        });
      }
    });

    return {
      orderCart: {
        lineItems: expandedLineItems,
        orderType: {
          taxable: 'false',
          isDefault: 'false',
          filterCategories: 'false',
          isHidden: 'false',
          isDeleted: 'false',
        },
        groupLineItems: 'true',
        discounts: [],
        source: 'online',
        isOnlineOrder: true,
      },
    };
  };

  // For backwards compatibility with existing components
  const getProcessedCartItems = () => {
    // Minimal logging - just record this once for performance
    if (cartItems.length > 0) {
      addBreadcrumb('Processing cart items for display', {
        itemCount: cartItems.length,
      });
    }

    return cartItems.map((item) => {
      // Use normalized modifiers from our utility
      const normalizedModifiers = normalizeModifiers(item);

      // Calculate price using our utility for consistency
      const itemPrice = calculateItemPrice(item, normalizedModifiers);
      const subtotal = calculateSubtotal(item, normalizedModifiers);

      // Return standardized format for display
      return {
        id: item.cartItemId,
        name: item.name || item.baseItem?.name || 'Unknown Item',
        price: itemPrice,
        quantity: item.quantity,
        subtotal: subtotal,
        modifiers: normalizedModifiers,
        displayPrice: item.displayPrice,
        originalItem: item.originalItem || item,
      };
    });
  };

  // Helper functions to assist with legacy code migration

  /**
   * Find a cart item by its base item ID
   * @param {string} baseItemId - The base item ID to find
   * @returns {Object|null} The found cart item or null if not found
   */
  const findCartItemByBaseId = (baseItemId) => {
    if (!baseItemId) return null;
    return cartItems.find((item) => item.baseItem?.id === baseItemId);
  };

  /**
   * Find all cart items that match a base item ID
   * @param {string} baseItemId - The base item ID to match
   * @returns {Array} Array of matching cart items
   */
  const findCartItemsByBaseId = (baseItemId) => {
    if (!baseItemId) return [];
    return cartItems.filter((item) => item.baseItem?.id === baseItemId);
  };

  /**
   * Find a cart item by cartItemId or by baseItemId as fallback
   * Helps transition from legacy item ID to cartItemId
   * @param {Object} item - Item with id or cartItemId
   * @returns {Object|null} The found cart item or null
   */
  const findCartItem = (item) => {
    // First try to find by cartItemId (direct match)
    if (item.cartItemId) {
      const directMatch = cartItems.find(
        (cartItem) => cartItem.cartItemId === item.cartItemId,
      );
      if (directMatch) {
        return directMatch;
      }
    }

    // If we have an id that might be a cartItemId, try that
    if (item.id) {
      const idMatch = cartItems.find(
        (cartItem) => cartItem.cartItemId === item.id,
      );
      if (idMatch) {
        return idMatch;
      }
    }

    // Last resort: try to match by base item id
    const baseId = item.id || (item.baseItem ? item.baseItem.id : null);
    return findCartItemByBaseId(baseId);
  };

  // Calculate cart total for context consumers
  const cartTotal = calculateCartTotal(cartItems);

  // Context value with complete API
  const contextValue = {
    // Cart data
    cartItems,
    cartTotal,
    cartLoaded,

    // Core methods
    addToCart,
    updateCartItem,
    updateCartItemQuantity,
    removeCartItem,
    getCloverOrderData,
    getProcessedCartItems,

    // Migration helper functions
    findCartItem,
    findCartItemByBaseId,
    findCartItemsByBaseId,

    // Utility methods
    clearCart,
    getTotalItems,

    // Helper functions exposed for components
    formatPrice: (price) => formatPrice(price),
  };

  return (
    <CartContext.Provider value={contextValue}>{children}</CartContext.Provider>
  );
};

export default CartContext;
